/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.14.0
 */

import produce from 'immer';
import { Reducer } from 'redux';

import { ETextBlockStatus } from '../../components/common/TextBlockStatus/textBlockStatusTypes';
import { WIZARD_STORAGE_CLEAR_STEPS } from '../../modules/wizard/wizardConstants';
import { TWizardAdditionalAccountsActions } from './types/wizardAdditionalAccountsActionTypes';
import { IWizardAdditionalAccountsValidation } from './types/wizardAdditionalAccountsTypes';
import {
    initWizardAdditionalAccountsState,
    WIZARD_ADDITIONAL_ACCOUNTS_SET_PARAMS,
    WIZARD_ADDITIONAL_ACCOUNTS_SET_STEP_VALID,
} from './wizardAdditionalAccountsConstant';

export interface IWizardAdditionalAccountsSelectedTypes {
    isActivateAdditionalAccounts: boolean;
    isDeactivateAdditionalAccounts: boolean;

    isActivatePersonalAccount: boolean;
    isActivateContentAccount: boolean;
    isDeactivatePersonalAccount: boolean;
    isDeactivateContentAccount: boolean;
}

export interface IWizardAdditionalAccountsOrderError {
    errorCode: string;
    errorMessage: string;
}

export interface IWizardAdditionalAccountsOrderSubscriber {
    subsId: number;
    charge: number;
    msisdn: string;
    errorMessage: string;
    personalAccount?: {
        status: Exclude<ETextBlockStatus, ETextBlockStatus.CHANGE>;
        errors: IWizardAdditionalAccountsOrderError[];
    };
    contentAccount?: {
        status: Exclude<ETextBlockStatus, ETextBlockStatus.CHANGE>;
        errors: IWizardAdditionalAccountsOrderError[];
    };
    status: Exclude<ETextBlockStatus, ETextBlockStatus.CHANGE>;
}

export interface IWizardAdditionalAccountsOrderResult {
    sumCharge: number;
    orderSubs: IWizardAdditionalAccountsOrderSubscriber[];
}

export interface IWizardAdditionalAccountsReducer {
    activateTypes: IWizardAdditionalAccountType[];
    deactivateTypes: IWizardAdditionalAccountType[];
    isAgreementCheckboxEnabled: boolean;
    isOnlineCheckProceeded: boolean;
    offerLink: string;
    orderResult: IWizardAdditionalAccountsOrderResult;
    removedSubscribers: number[];
    selected: IWizardAdditionalAccountsSelectedTypes;
    subscriberIds: number[];
    validation: IWizardAdditionalAccountsValidation;
}

export interface IWizardAdditionalAccountType {
    def: string;
    isEnabled: boolean;
}

const wizardAdditionalAccountsReducer: Reducer<IWizardAdditionalAccountsReducer, TWizardAdditionalAccountsActions> = (
    state = initWizardAdditionalAccountsState,
    action: TWizardAdditionalAccountsActions,
) =>
    produce(state, (draft) => {
        switch (action.type) {
            case WIZARD_ADDITIONAL_ACCOUNTS_SET_PARAMS:
                Object.assign(draft, { ...draft, ...action.payload });
                break;
            case WIZARD_ADDITIONAL_ACCOUNTS_SET_STEP_VALID:
                draft.validation.isValid = action.payload.isStepValid;
                draft.validation.error = action.payload.validateError;
                break;

            case WIZARD_STORAGE_CLEAR_STEPS:
                Object.assign(draft, initWizardAdditionalAccountsState);
                break;
        }
    });

export default wizardAdditionalAccountsReducer;
